import type { AdifRecordType } from '@/types';

export interface ExtractedRefs {
  potaRef?: string;
  sotaRef?: string;
}

/**
 * Extract POTA/SOTA references from a single ADIF record (as decoded from a
 * WSJT-X LOGGED_ADIF message). Prefers the dedicated POTA_REF/SOTA_REF fields,
 * falling back to SIG_INFO when SIG identifies the program.
 */
export function extractRefsFromAdifRecord(record: AdifRecordType): ExtractedRefs {
  const refs: ExtractedRefs = {};
  const sig = (record.sig || '').toUpperCase();

  if (record.potaRef) {
    refs.potaRef = record.potaRef.trim();
  } else if (sig === 'POTA' && record.sigInfo) {
    refs.potaRef = record.sigInfo.trim();
  }

  if (record.sotaRef) {
    refs.sotaRef = record.sotaRef.trim();
  } else if (sig === 'SOTA' && record.sigInfo) {
    refs.sotaRef = record.sigInfo.trim();
  }

  return refs;
}

/**
 * Resolve the "Their Park" / "Their Summit" values for a logged QSO using the
 * documented source precedence:
 *   1. QSO_LOGGED exchangeReceived (when non-empty)
 *   2. LOGGED_ADIF POTA_REF / SOTA_REF (or SIG_INFO via SIG)
 *
 * exchangeReceived has no program identifier of its own, so it is routed to
 * whichever field the active logbook actually defines (park preferred).
 */
export function resolveTheirRefs(params: {
  exchangeReceived?: string;
  adifRefs?: ExtractedRefs | null;
  hasPota: boolean;
  hasSota: boolean;
}): ExtractedRefs {
  const { exchangeReceived, adifRefs, hasPota, hasSota } = params;
  const result: ExtractedRefs = {};

  // 1. exchangeReceived takes precedence.
  const exchange = (exchangeReceived || '').trim();
  if (exchange) {
    if (hasPota) {
      result.potaRef = exchange;
    } else if (hasSota) {
      result.sotaRef = exchange;
    }
  }

  // 2. Fall back to correlated LOGGED_ADIF data when not already set.
  if (adifRefs) {
    if (hasPota && !result.potaRef && adifRefs.potaRef) {
      result.potaRef = adifRefs.potaRef;
    }
    if (hasSota && !result.sotaRef && adifRefs.sotaRef) {
      result.sotaRef = adifRefs.sotaRef;
    }
  }

  return result;
}
